import { Alert, Button, Space, Typography, message } from 'antd';
import React, { useRef } from 'react';
import ReactMarkdown from 'react-markdown';

import { NoteCardEditorShell } from '@/components/common/NoteCardEditorShell';
import { TagManager } from '@/components/common/TagManager';
import {
  CrepeMarkdownField,
  type CrepeMarkdownFieldHandle,
} from '@/components/editor/CrepeMarkdownField';
import { useAssetEditor } from '@/hooks/useAssetEditor';
import { LinkIcon as LinkOutlined } from '@/icons';

const { Text } = Typography;

const isTabularDocumentSourceUrl = (value: string | null): boolean => {
  if (!value) {
    return false;
  }
  try {
    const parsed = new URL(value);
    const pathName = parsed.pathname.toLowerCase();
    return pathName.endsWith('.xlsx') || pathName.endsWith('.csv');
  } catch {
    const lowered = value.toLowerCase();
    return lowered.includes('.xlsx') || lowered.includes('.csv');
  }
};

export interface CreateNotePageProps {
  assetSessionId: string;
  accessToken?: string | null;
  clientId?: string;
  projectId?: string;
  onBackToAssetLibrary: () => void;
  /** Called after every successful save with the persisted card_id. */
  onSaved?: (cardId: string) => void;
}

export const CreateNotePage: React.FC<CreateNotePageProps> = ({
  assetSessionId,
  accessToken,
  clientId,
  projectId,
  onBackToAssetLibrary: _onBackToAssetLibrary,
  onSaved,
}) => {
  const crepeFieldRef = useRef<CrepeMarkdownFieldHandle>(null);
  const editor = useAssetEditor({
    assetSessionId,
    accessToken,
    clientId,
    projectId,
  });

  const handleSave = async (): Promise<void> => {
    const latestMarkdown = crepeFieldRef.current?.getMarkdown() ?? editor.markdown;
    const result = await editor.save(latestMarkdown);
    if (result.error) {
      void message.error(result.error);
      return;
    }
    if (result.saved && result.cardId) {
      const successMessage = editor.sourceType === 'document' ? 'Summary saved' : 'Asset saved';
      void message.success(successMessage);
      onSaved?.(result.cardId);
    }
  };

  const handleEditorShortcut = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 's') {
      event.preventDefault();
      if (editor.isDirty) {
        void handleSave();
      }
    }
  };

  const showTabularDataSafety =
    editor.sourceType === 'document' && isTabularDocumentSourceUrl(editor.sourceUrl);

  return (
    <CreateNotePageView
      accessToken={accessToken}
      assetSessionId={assetSessionId}
      clientId={clientId}
      crepeFieldRef={crepeFieldRef}
      editor={editor}
      handleEditorShortcut={handleEditorShortcut}
      handleSave={handleSave}
      projectId={projectId}
      showTabularDataSafety={showTabularDataSafety}
    />
  );
};

interface CreateNotePageViewProps {
  accessToken?: string | null;
  assetSessionId: string;
  clientId?: string;
  crepeFieldRef: React.RefObject<CrepeMarkdownFieldHandle>;
  editor: ReturnType<typeof useAssetEditor>;
  handleEditorShortcut: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  handleSave: () => Promise<void>;
  projectId?: string;
  showTabularDataSafety: boolean;
}

const CreateNotePageView: React.FC<CreateNotePageViewProps> = ({
  accessToken,
  assetSessionId,
  clientId,
  crepeFieldRef,
  editor,
  handleEditorShortcut,
  handleSave,
  projectId,
  showTabularDataSafety,
}) => {
  return (
    <div className="page-stack create-note-page">
      <div className="page-stack create-note-page">
        <NoteCardEditorShell
          dataAssetSessionId={assetSessionId}
          titleRow={
            <div className="create-note-page__title-row">
              <input
                aria-label="Page title"
                className="create-note-title-input"
                placeholder="Enter title..."
                value={editor.title}
                disabled={!editor.isEditable}
                onChange={(event) => editor.setTitle(event.target.value)}
              />
              <Text
                data-testid="asset-save-state"
                className="create-note-save-indicator"
                type={editor.isEditable && editor.statusLabel !== 'Saved' ? 'warning' : 'success'}
              >
                {editor.isEditable ? editor.statusLabel : 'Saved'}
              </Text>
            </div>
          }
          tagRow={
            <TagManager
              tags={editor.tags}
              accessToken={accessToken ?? ''}
              clientId={clientId ?? ''}
              projectId={projectId}
              cardId={editor.persistedCardId}
              localOnly={!editor.persistedCardId}
              onTagsChange={editor.setTags}
            />
          }
          betweenTagsAndTip={
            editor.loadError || showTabularDataSafety ? (
              <Space direction="vertical" size="small" style={{ width: '100%', marginTop: 12 }}>
                {editor.loadError ? (
                  <Alert type="error" showIcon message={editor.loadError} />
                ) : null}
                {showTabularDataSafety ? (
                  <Alert
                    type="warning"
                    showIcon
                    message="Data safety"
                    description="Do not upload PI, PII, or PHI. This document is extracted and indexed for AI search and summarization."
                  />
                ) : null}
              </Space>
            ) : null
          }
          tipRow={
            editor.isEditable ? (
              <div
                style={{
                  display: 'flex',
                  justifyContent: editor.sourceUrl ? 'space-between' : 'flex-end',
                  alignItems: 'center',
                }}
              >
                {editor.sourceUrl && (
                  <Typography.Link
                    href={editor.sourceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View original document <LinkOutlined />
                  </Typography.Link>
                )}
                <Text type="secondary" className="create-note-page__tip">
                  Milkdown tip: use slash commands in the editor with <code>/</code>.
                </Text>
              </div>
            ) : null
          }
          editorRegion={
            editor.isEditable ? (
              <div
                onKeyDownCapture={handleEditorShortcut}
                className="create-note-page__editor-region"
              >
                {editor.editorReady ? (
                  <CrepeMarkdownField
                    ref={crepeFieldRef}
                    bootKey={assetSessionId}
                    defaultMarkdown={editor.markdown}
                    onMarkdownChange={editor.setMarkdown}
                    onReady={editor.hydrateMarkdown}
                  />
                ) : (
                  <div className="create-note-page__editor-loading" aria-busy="true">
                    Loading…
                  </div>
                )}
              </div>
            ) : (
              <div className="create-note-page__editor-region">
                <Text type="secondary">Summary</Text>
                <div style={{ marginTop: 8 }}>
                  <ReactMarkdown>
                    {(editor.summaryText || editor.markdown).trim() || 'No summary available.'}
                  </ReactMarkdown>
                </div>
                {editor.sourceUrl ? (
                  <div style={{ marginTop: 12 }}>
                    <Typography.Link
                      href={editor.sourceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View original document <LinkOutlined />
                    </Typography.Link>
                  </div>
                ) : null}
              </div>
            )
          }
          footer={
            <Space style={{ justifyContent: 'flex-end', width: '100%' }}>
              {editor.isEditable ? (
                <Button
                  type="primary"
                  loading={editor.isSaving}
                  onClick={() => void handleSave()}
                  disabled={!editor.canSave}
                >
                  {editor.sourceType === 'document' ? 'Save Summary' : 'Save Note'}
                </Button>
              ) : null}
            </Space>
          }
        />
      </div>
    </div>
  );
};

export default CreateNotePage;
